/**
 * World-anchored text, drawn on the GPU.
 *
 * A batch of quads that all share one atlas and one pipeline: glyphs, the
 * plates behind them and the leader lines pointing at what they describe. Each
 * quad carries a WORLD anchor and a SCREEN offset, so it follows a thing
 * through the world while staying the size and shape a label has to be to be
 * read. See `text.wgsl` for why that split is the design rather than a
 * shortcut.
 *
 * Everything accumulates into one vertex buffer and goes out in one draw. That
 * is the whole reason the atlas exists — the HUD's 2D canvas already draws
 * sharp text, and what it cannot do is a hundred labels without a hundred
 * state changes, or any label at all in a layer underneath the HUD chrome.
 */

import textCode from './shaders/text.wgsl?raw';
import {
  createFontAtlas, getGlyph, measureText, SDF_SPREAD, type FontAtlas,
} from './font';

/** anchor(3) + offset(2) + uv(2) + colour(4) + mode(1) */
const FLOATS_PER_VERTEX = 12;
const VERTS_PER_QUAD = 6;
/**
 * Quads in one frame's batch.
 *
 * A callout is roughly forty — two lines of a dozen glyphs each, a plate, a
 * three-segment leader and a ring — so a hundred of those is far more than a
 * ping can put on screen. The headroom above that is the performance readout,
 * which is thirty lines of sixty monospaced characters and therefore nearly two
 * thousand quads on its own: a debug panel is by far the most text this engine
 * ever draws at once, and it is the one place where running out would be
 * silently misleading rather than merely ugly.
 *
 * Overflow drops the tail rather than growing mid-frame: a reallocation inside
 * a draw is a stall, and a missing label is a smaller problem than a hitch.
 */
const MAX_QUADS = 12288;

export interface TextStyle {
  /** Linear-ish CSS-style rgba, 0..1. */
  colour: [number, number, number, number];
  /** Display pixels; the atlas is rasterised at `FONT_SIZE` and scales from there. */
  size: number;
  /** Extra advance per glyph, matching the HUD's letter-spacing. */
  tracking?: number;
  /** 'left' puts `offset` at the first glyph's origin, 'right' at the last's. */
  align?: 'left' | 'right' | 'center';
}

export class TextBatch {
  readonly atlas: FontAtlas;
  private device: GPUDevice;
  private pipeline: GPURenderPipeline;
  private uniform: GPUBuffer;
  private uniformData = new Float32Array(24);
  private bind: GPUBindGroup;
  private vbo: GPUBuffer;
  private data: Float32Array<ArrayBuffer>;
  private quads = 0;
  /**
   * 1 while emitting SCREEN-anchored quads, 0 for world-anchored ones.
   *
   * A mode rather than a parameter on every call: screen-space text arrives in
   * blocks — a whole panel, a whole readout — and threading a boolean through
   * `text`, `rect`, `line` and `quad` at every call site would be four times
   * the noise for a thing that changes twice a frame. Set it, emit, clear it.
   */
  private mode = 0;
  /** Reported once. A batch that silently truncates is a batch nobody trusts. */
  private warned = false;

  constructor(device: GPUDevice, format: GPUTextureFormat, samples = 1) {
    this.device = device;
    this.atlas = createFontAtlas(device);
    this.data = new Float32Array(MAX_QUADS * VERTS_PER_QUAD * FLOATS_PER_VERTEX);

    this.vbo = device.createBuffer({
      size: this.data.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      label: 'text',
    });
    this.uniform = device.createBuffer({
      size: this.uniformData.byteLength,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      label: 'text',
    });

    const module = device.createShaderModule({ code: textCode, label: 'text' });
    this.pipeline = device.createRenderPipeline({
      label: 'text',
      // `auto`, and it is safe here in a way it is not for the scene pipelines:
      // this module has exactly one bind group and this file owns every entry
      // in it. The explicit layouts elsewhere exist because those groups are
      // shared across pipelines that read different subsets.
      layout: 'auto',
      vertex: {
        module,
        entryPoint: 'vsText',
        buffers: [{
          arrayStride: FLOATS_PER_VERTEX * 4,
          attributes: [
            { shaderLocation: 0, offset: 0, format: 'float32x3' },
            { shaderLocation: 1, offset: 12, format: 'float32x2' },
            { shaderLocation: 2, offset: 20, format: 'float32x2' },
            { shaderLocation: 3, offset: 28, format: 'float32x4' },
            { shaderLocation: 4, offset: 44, format: 'float32' },
          ],
        }],
      },
      fragment: {
        module,
        entryPoint: 'fsText',
        targets: [{
          format,
          // Straight alpha, not premultiplied: the fragment returns the tint's
          // own rgb with coverage in alpha, which is what lets one white atlas
          // be drawn in any colour.
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' },
          },
        }],
      },
      primitive: { topology: 'triangle-list' },
      multisample: { count: samples },
    });

    this.bind = device.createBindGroup({
      layout: this.pipeline.getBindGroupLayout(0),
      entries: [
        { binding: 0, resource: { buffer: this.uniform } },
        { binding: 1, resource: this.atlas.sampler },
        { binding: 2, resource: this.atlas.texture.createView() },
        { binding: 3, resource: this.atlas.sdf.createView() },
      ],
      label: 'text',
    });
  }

  /**
   * Emit in SCREEN space until `world()` is called: `offset` becomes display
   * pixels from the top-left and the anchor is ignored. Pass 0, 0, 0 for it.
   */
  screen() { this.mode = 1; }
  /** Back to world-anchored emission. */
  world() { this.mode = 0; }

  /** Start a frame. `viewProj` is the unjittered one; jitter would crawl text. */
  begin(viewProj: Float32Array | number[], width: number, height: number,
        dpr: number, sdf = true) {
    this.quads = 0;
    // A frame that threw part way through an overlay must not leave the next
    // one emitting its world callouts in screen space.
    this.mode = 0;
    for (let i = 0; i < 16; i++) this.uniformData[i] = viewProj[i]!;
    this.uniformData[16] = width;
    this.uniformData[17] = height;
    this.uniformData[18] = dpr;
    // Which atlas the fragment derives coverage from. Both are resident, so
    // this is a branch rather than a rebuild and the two can be compared on
    // consecutive frames.
    this.uniformData[19] = sdf ? 1 : 0;
    // The atlas in texels, and what one texel is worth in field units. The
    // fragment needs both to work out a screen pixel's edge width from
    // geometry rather than from the sampled value — see `fsText`.
    this.uniformData[20] = this.atlas.atlasWidth;
    this.uniformData[21] = this.atlas.atlasHeight;
    this.uniformData[22] = 1 / (2 * SDF_SPREAD);
    this.uniformData[23] = 0;
  }

  get count(): number { return this.quads; }

  /**
   * One quad, in display pixels around a world anchor.
   *
   * `u0..v1` name a rect in the atlas; passing the solid block's uv makes this
   * a plain filled rectangle, which is what the plates and the leader
   * segments are.
   */
  quad(
    ax: number, ay: number, az: number,
    x0: number, y0: number, x1: number, y1: number,
    u0: number, v0: number, u1: number, v1: number,
    col: [number, number, number, number],
  ) {
    if (this.quads >= MAX_QUADS) {
      if (!this.warned) {
        this.warned = true;
        console.warn(`[abyss] text batch full at ${MAX_QUADS} quads; the rest of this frame is dropped`);
      }
      return;
    }
    let o = this.quads * VERTS_PER_QUAD * FLOATS_PER_VERTEX;
    const d = this.data;
    const push = (px: number, py: number, pu: number, pv: number) => {
      d[o] = ax; d[o + 1] = ay; d[o + 2] = az;
      d[o + 3] = px; d[o + 4] = py;
      d[o + 5] = pu; d[o + 6] = pv;
      d[o + 7] = col[0]; d[o + 8] = col[1]; d[o + 9] = col[2]; d[o + 10] = col[3];
      d[o + 11] = this.mode;
      o += FLOATS_PER_VERTEX;
    };
    push(x0, y0, u0, v0); push(x1, y0, u1, v0); push(x1, y1, u1, v1);
    push(x0, y0, u0, v0); push(x1, y1, u1, v1); push(x0, y1, u0, v1);
    this.quads++;
  }

  /** A filled rectangle at the anchor, in display pixels. */
  rect(ax: number, ay: number, az: number,
       x0: number, y0: number, x1: number, y1: number,
       col: [number, number, number, number]) {
    const s = this.atlas.solid;
    this.quad(ax, ay, az, x0, y0, x1, y1, s.u, s.v, s.u, s.v, col);
  }

  /**
   * A line as a quad, since a triangle-list pipeline has no lines and adding a
   * second topology would mean a second pipeline for one primitive.
   *
   * Thickness is honest rather than a `line-list`'s one-pixel default, which is
   * the other reason: a leader at 1 device pixel disappears on a phone.
   */
  line(ax: number, ay: number, az: number,
       x0: number, y0: number, x1: number, y1: number,
       w: number, col: [number, number, number, number]) {
    const dx = x1 - x0, dy = y1 - y0;
    const len = Math.hypot(dx, dy);
    if (len < 1e-4) return;
    const nx = (-dy / len) * w * 0.5, ny = (dx / len) * w * 0.5;
    // THE RAMP ACROSS THE WIDTH, not the solid block.
    //
    // This pass draws after every antialiasing stage in the frame, so a hard
    // quad edge is the last word — and a leader near one pixel wide then
    // covers a pixel centre on some frames and misses on others, which blinks.
    // The ramp makes the edge analytically soft for the cost of a uv.
    const r = this.atlas.ramp;
    if (this.quads >= MAX_QUADS) return;
    let o = this.quads * VERTS_PER_QUAD * FLOATS_PER_VERTEX;
    const d = this.data;
    const push = (px: number, py: number, u: number) => {
      d[o] = ax; d[o + 1] = ay; d[o + 2] = az;
      d[o + 3] = px; d[o + 4] = py;
      d[o + 5] = u; d[o + 6] = r.v0;
      d[o + 7] = col[0]; d[o + 8] = col[1]; d[o + 9] = col[2]; d[o + 10] = col[3];
      d[o + 11] = this.mode;
      o += FLOATS_PER_VERTEX;
    };
    push(x0 + nx, y0 + ny, r.u0); push(x1 + nx, y1 + ny, r.u0); push(x1 - nx, y1 - ny, r.u1);
    push(x0 + nx, y0 + ny, r.u0); push(x1 - nx, y1 - ny, r.u1); push(x0 - nx, y0 - ny, r.u1);
    this.quads++;
  }

  /** Advance width of `text` at `style.size`, in display pixels. */
  width(text: string, style: TextStyle): number {
    const k = style.size / this.atlas.glyphHeight;
    return measureText(this.atlas, text, (style.tracking ?? 0) / k) * k;
  }

  /**
   * A run of glyphs, with `x, y` the LEFT end of its baseline in display
   * pixels from the anchor.
   */
  text(ax: number, ay: number, az: number, text: string,
       x: number, y: number, style: TextStyle) {
    // The atlas is rasterised at one size and every other size is this ratio.
    // Against `glyphHeight` rather than `FONT_SIZE`, because the cell carries
    // the padding and the caller is asking for a cell height.
    const k = style.size / this.atlas.glyphHeight;
    const track = style.tracking ?? 0;
    let cx = x;
    const align = style.align ?? 'left';
    if (align !== 'left') {
      const w = this.width(text, style);
      cx -= align === 'right' ? w : w * 0.5;
    }
    const top = y - this.atlas.baselineOffset * k;
    for (const ch of text) {
      const g = getGlyph(this.atlas, ch.codePointAt(0)!);
      if (!g) { cx += track; continue; }
      const w = g.width * k, h = this.atlas.glyphHeight * k;
      // The cell is drawn whole, padding included: the padding is transparent,
      // and trimming it would mean the uv rect and the quad had to agree about
      // a sub-pixel inset in two places instead of none.
      const gx = cx - g.xOffset * k;
      this.quad(ax, ay, az, gx, top, gx + w, top + h,
                g.u0, g.v0, g.u1, g.v1, style.colour);
      cx += g.advance * k + track;
    }
  }

  /** Upload and draw. Does nothing when the frame put no text up. */
  flush(pass: GPURenderPassEncoder) {
    if (this.quads === 0) return;
    this.device.queue.writeBuffer(this.uniform, 0, this.uniformData);
    const floats = this.quads * VERTS_PER_QUAD * FLOATS_PER_VERTEX;
    this.device.queue.writeBuffer(this.vbo, 0, this.data, 0, floats);
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bind);
    pass.setVertexBuffer(0, this.vbo);
    pass.draw(this.quads * VERTS_PER_QUAD);
  }
}
